// AssessmentForm.tsx — what the Community Liaison Team records at a hardship visit.
//
// WHY THIS IS NOT A DROPDOWN. Category 1 suppresses fee-bearing and legal stages under a named
// rule. "On what basis?" will be asked — by an auditor, by a resident disputing a decision, and by
// the officer who inherits the case in six months. A single hardship code cannot answer it. What
// answers it is what the officer actually gathers at the door.
//
// THREE THINGS THIS SCREEN REFUSES TO FUDGE:
//
//   INCOME NOT STATED IS NOT ZERO. A household that will not give a figure is an ordinary outcome
//   of a hardship visit, and a 0 would be read as destitution. Left blank it stays NULL, and the
//   eligibility line says it cannot screen rather than guessing.
//
//   A REFERRAL NEEDS CONSENT. Referring a household to SSO, SSA or a VWO discloses their personal
//   circumstances to a third party. The officer's good intentions are not a lawful basis for that,
//   so the referral chips stay disabled until consent is ticked, and the server refuses it anyway.
//
//   "NO HARDSHIP FOUND" IS A RESULT, NOT A FAILURE. It lifts the hold a Property Officer's
//   hand-off put on the account and sends the case back to collection with a reason, instead of
//   leaving a household in limbo because nobody wanted to write it down.
import { useState } from 'react'
import { api, ApiError } from '../../lib/api'
import { DRIVERS, money } from '../../lib/format'

type Result = Awaited<ReturnType<typeof api.ams.logAssessment>>

const EVIDENCE: Array<{ key: string; label: string }> = [
  { key: 'income_declaration', label: 'Income declaration' },
  { key: 'payslip_cpf', label: 'Payslip / CPF' },
  { key: 'medical_certificate', label: 'Medical certificate' },
  { key: 'job_search_proof', label: 'Job search proof' },
  { key: 'nric_sighted', label: 'NRIC sighted' },
  { key: 'bankruptcy_order', label: 'Bankruptcy order' },
  { key: 'caregiver_record', label: 'Caregiver record' },
]

const REFERRALS: Array<{ key: string; label: string }> = [
  { key: 'sso', label: 'SSO' },
  { key: 'ssa', label: 'SSA' },
  { key: 'vwo', label: 'VWO' },
  { key: 'employment_support', label: 'Employment support' },
  { key: 'medifund', label: 'MediFund' },
]

const OFFERS: Array<{ key: string; label: string }> = [
  { key: 'instalment', label: 'Instalment plan' },
  { key: 'deferral', label: 'Deferral' },
  { key: 'agency_only', label: 'Agency help only' },
  { key: 'none', label: 'Nothing agreed' },
]

/**
 * Per-head income, and what it is safe to say about it.
 *
 * NOT AN ELIGIBILITY DECISION. SSO eligibility is decided by SSO against criteria we do not hold,
 * and a screen that said "eligible" would be inventing a fact into a record an officer then repeats
 * to a family. What this computes is the arithmetic the officer would otherwise do in their head,
 * with the threshold named so the reader can check it — and when income is not stated it says so
 * rather than assuming.
 */
function incomeNote(incomeCents: number | null, adults: number, children: number): string {
  const heads = adults + children
  if (incomeCents === null) return 'Income not stated, so per-head income cannot be worked out.'
  if (heads === 0) return 'Household size not stated, so per-head income cannot be worked out.'
  const perHead = incomeCents / heads
  return `${money(Math.round(perHead))} per person per month, across ${heads}. `
       + (perHead <= 100000
          ? 'Below the $1,000 per-head figure SSO commonly uses — worth referring. SSO decides.'
          : 'Above the $1,000 per-head figure SSO commonly uses. SSO still decides.')
}

export default function AssessmentForm({ org, caseId, visitId, resident, onDone, onBack }:
  { org: string; caseId: string; visitId?: string; resident: string
    onDone: (r: Result) => void; onBack: () => void }) {
  const [found, setFound] = useState<boolean | null>(null)
  const [code, setCode] = useState('')
  const [adults, setAdults] = useState('1')
  const [children, setChildren] = useState('0')
  const [income, setIncome] = useState('')
  const [evidence, setEvidence] = useState<string[]>([])
  const [offer, setOffer] = useState('')
  const [instalment, setInstalment] = useState('')
  const [referrals, setReferrals] = useState<string[]>([])
  const [consent, setConsent] = useState(false)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const toggle = (list: string[], set: (v: string[]) => void, key: string) =>
    set(list.includes(key) ? list.filter(k => k !== key) : [...list, key])

  const incomeCents = income.trim() === '' ? null : Math.round(parseFloat(income) * 100)

  // Withdrawing consent must withdraw the referrals that relied on it. Leaving them ticked with
  // the box unticked would submit a referral the resident has not agreed to — the server refuses
  // it, but the screen should not have offered it.
  const setConsentSafely = (v: boolean) => {
    setConsent(v)
    if (!v) setReferrals([])
  }

  const ready = found === false
    || (found === true && !!code && !!offer
        && (offer !== 'instalment' || !!instalment))

  async function save() {
    setBusy(true); setErr(null)
    try {
      onDone(await api.ams.logAssessment(org, caseId, {
        hardshipFound: found === true,
        hardshipCode: found === true ? code : null,
        visitId: visitId ?? null,
        adults: parseInt(adults, 10) || 0,
        children: parseInt(children, 10) || 0,
        monthlyIncomeCents: incomeCents,
        evidence,
        offer: found === true ? offer : null,
        instalmentCents: offer === 'instalment' && instalment
          ? Math.round(parseFloat(instalment) * 100) : null,
        referrals,
        consentToShare: consent,
        notes: notes || null,
      }))
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : String(e))
    } finally { setBusy(false) }
  }

  return (
    <div className="fld-work">
      <div className="fld-head fld-context">
        <button className="fld-back" onClick={onBack}>‹ Visits</button>
        <b>{resident}</b>
        <small>Hardship assessment</small>
      </div>
      <div className="fld-body fld-form">
        <p className="fld-tip">
          You are here to understand, not to collect. Start by asking what has changed.
        </p>

        <Q label="Is there hardship here?">
          <Chip on={found === true} onClick={() => setFound(true)}>Yes</Chip>
          <Chip on={found === false} onClick={() => setFound(false)}>No hardship found</Chip>
        </Q>

        {found === false && (
          <p className="fld-note">
            Recorded as assessed with no hardship. Any hold on this account is lifted and the case
            goes back to ordinary collection — which is a real result, not a failure.
          </p>
        )}

        {found === true && (
          <>
            <Q label="Main reason paying is hard">
              {Object.entries(DRIVERS).map(([k, v]) => (
                <Chip key={k} on={code === k} onClick={() => setCode(k)}>{k} {v}</Chip>
              ))}
            </Q>

            <Q label="Household">
              <div className="fld-inline">
                <label className="asm-num">Adults
                  <input type="number" min="0" value={adults}
                         onChange={e => setAdults(e.target.value)} />
                </label>
                <label className="asm-num">Children
                  <input type="number" min="0" value={children}
                         onChange={e => setChildren(e.target.value)} />
                </label>
              </div>
              <label className="asm-num">Monthly household income
                <input inputMode="decimal" placeholder="Leave blank if not stated"
                       value={income} onChange={e => setIncome(e.target.value)} />
              </label>
              {/* Arithmetic, with its basis named. Never a verdict — see incomeNote. */}
              <p className="fld-note">
                {incomeNote(incomeCents, parseInt(adults, 10) || 0, parseInt(children, 10) || 0)}
              </p>
            </Q>

            <Q label="Evidence sighted">
              {EVIDENCE.map(e => (
                <Chip key={e.key} on={evidence.includes(e.key)}
                      onClick={() => toggle(evidence, setEvidence, e.key)}>{e.label}</Chip>
              ))}
              {evidence.length === 0 && (
                <p className="fld-note">
                  Nothing sighted yet. The assessment still saves — it is recorded as resting on
                  what you were told, which is what an auditor will read it as.
                </p>
              )}
            </Q>

            <Q label="What was offered?">
              {OFFERS.map(o => (
                <Chip key={o.key} on={offer === o.key} onClick={() => setOffer(o.key)}>
                  {o.label}
                </Chip>
              ))}
              {offer === 'instalment' && (
                <div className="fld-inline">
                  <input inputMode="decimal" placeholder="Amount / month"
                         value={instalment} onChange={e => setInstalment(e.target.value)} />
                </div>
              )}
            </Q>

            <Q label="Referrals">
              <label className="asm-consent">
                <input type="checkbox" checked={consent}
                       onChange={e => setConsentSafely(e.target.checked)} />
                <span>
                  <b>The resident agrees to their circumstances being shared with an agency.</b>
                  <small>
                    A referral tells a third party about this household. Without this, there is no
                    basis for it — so the referrals below stay locked until it is ticked.
                  </small>
                </span>
              </label>
              {REFERRALS.map(r => (
                <Chip key={r.key} on={referrals.includes(r.key)} disabled={!consent}
                      onClick={() => toggle(referrals, setReferrals, r.key)}>{r.label}</Chip>
              ))}
              {referrals.length > 0 && (
                <p className="fld-note">
                  Referrals are <b>recorded</b> on this case. The system does not send them — you
                  still have to make them.
                </p>
              )}
            </Q>
          </>
        )}

        <Q label="Notes">
          <textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="What the family told you, in their words. This is what the next officer reads." />
        </Q>

        {err && <p className="fld-note err">Not saved: {err}</p>}
        <button className="fld-save" disabled={!ready || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save assessment'}
        </button>
      </div>
    </div>
  )
}

function Q({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fld-card static">
      <h4>{label}</h4>
      <div className="fld-chips">{children}</div>
    </div>
  )
}

function Chip({ on, onClick, disabled, children }:
  { on: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button className={`fld-chip${on ? ' on' : ''}`} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  )
}
